import type { GuideResponse, WorkshopState } from '../../api/types';
import { type IqScenario } from '../../config/content';
import {
  type DeepDiveBand,
  type DeepDivePhase,
  type DeepDivePlay,
  type DeepDiveQuestion,
  type DeepDiveScenario,
  type DeepDiveSection,
  type StakeholderId,
} from '../../config/deepDive';
import {
  resolveClosing,
  resolveDeepDiveScenarios,
  resolveDeepDiveStakeholders,
  resolveIqScenarios,
  resolvePlayById,
  resolveScenarioById,
  resolveSequencing,
  resolveSequencingFallback,
} from '../../api/resolveContent';

/**
 * What the deep-dive found, turned into the analysis the report is built from.
 *
 * The deep-dive is the only part of the workshop that measures the customer, so
 * the gap analysis and the recommended plays are derived from it rather than
 * authored separately. Everything here recomputes from `guideResponses`, which
 * means the report changes as the SE records more of the conversation.
 *
 * Scoring: a gap weighs 3 and a partial 1, so a play that several answers point
 * at outranks one a single answer points at. Covered scores nothing, and a
 * question asked without an answer scores nothing either. It is reported as an
 * open follow-up rather than a gap, because "they did not know" and "they do
 * not do it" are different findings and only one of them is a problem.
 *
 * Cues never score. They are the customer's own phrasing, kept so the write-up
 * can quote them.
 */

/** Stored band values. The config calls the same three gap/partial/covered. */
export type Band = 'red' | 'amber' | 'green';

export const BAND_OF: Record<Band, DeepDiveBand> = {
  red: 'gap',
  amber: 'partial',
  green: 'covered',
};

export const WEIGHT: Record<Band, number> = { red: 3, amber: 1, green: 0 };

export const BAND_LABEL: Record<Band, string> = {
  red: 'GAP',
  amber: 'PARTIAL',
  green: 'COVERED',
};

/**
 * The recording and scoring rules in one place, so the screen and the exported
 * document state them identically.
 *
 * This is appendix material. It explains how the ranking was produced, which
 * matters when someone challenges the order but does not belong above the
 * findings themselves.
 */
export const DEEP_DIVE_METHOD = {
  weights: [
    ['Response', 'Weight', 'Counts as'],
    ['GAP', '3', 'A finding to act on'],
    ['PARTIAL', '1', 'Partially addressed'],
    ['COVERED', '0', 'Already handled'],
    ['No answer', '0', 'An open follow-up, not a gap'],
  ] as string[][],
  rules: [
    'One response per question. GAP, PARTIAL and COVERED are mutually exclusive.',
    'A question asked without an answer is recorded separately and reported as a follow-up.',
    'Every response points at one or more plays. A play scores the weight of each response pointing at it, so a play several answers point at ranks above one a single answer points at.',
    'Ties break on how many separate questions raised the play.',
    'Phrases recorded against an answer are evidence, not score. They are the wording used in the room.',
    'Only the scenario this workshop ran is counted. Answers recorded under another scenario belong to a conversation that did not happen.',
  ],
} as const;

export type FindingStatus = 'gap' | 'partial' | 'covered' | 'open';

export const FINDING_STATUS_TEXT: Record<FindingStatus, string> = {
  gap: 'Gap',
  partial: 'Partially addressed',
  covered: 'Already covered',
  open: 'Open follow-up',
};

export interface DeepDiveFinding {
  id: string;
  /** Section short label. This is what the report record is titled. */
  topic: string;
  sectionId: string;
  who: StakeholderId[];
  phase: DeepDivePhase;
  phaseLabel: string;
  status: FindingStatus;
  weight: number;
  /** The phrases actually heard, in the customer's wording. */
  heard: string[];
  note: string;
}

/** One section's tally. This is what the exposure bars are drawn from. */
export interface SectionFinding {
  id: string;
  code: string;
  short: string;
  title: string;
  phase: DeepDivePhase;
  phaseLabel: string;
  gaps: number;
  partials: number;
  covered: number;
  open: number;
  unlogged: number;
  asked: number;
  total: number;
  /** Share of the answers given that landed badly, 0-100. */
  exposure: number;
}

export interface GroupFinding {
  id: DeepDivePhase;
  label: string;
  gaps: number;
  partials: number;
  covered: number;
  open: number;
  asked: number;
  total: number;
}

export interface StakeholderFinding {
  who: StakeholderId;
  label: string;
  tier: string;
  gaps: number;
  partials: number;
  covered: number;
  open: number;
  asked: number;
  total: number;
}

/** A recommendation, with the answers that produced it. */
export interface RankedPlay {
  play: DeepDivePlay;
  score: number;
  from: { id: string; status: 'gap' | 'partial'; topic: string }[];
}

export interface DeepDiveAnalysis {
  /** Which of the three scenarios this workshop ran, if one was chosen. */
  scenario: DeepDiveScenario | undefined;
  /** The one-line description of that scenario, for the report. */
  scenarioLabel: string;
  scenarioSetup: string;
  sections: SectionFinding[];
  groups: GroupFinding[];
  stakeholders: StakeholderFinding[];
  findings: DeepDiveFinding[];
  /** Every play the answers point at, heaviest first. */
  plays: RankedPlay[];
  /** The five to lead with. */
  topPlays: RankedPlay[];
  /** Everything below the top five. Detail, not headline. */
  restPlays: RankedPlay[];
  /** Asked but unanswered, reported separately from gaps. */
  followUps: DeepDiveFinding[];
  asked: number;
  total: number;
  gaps: number;
  partials: number;
  covered: number;
  open: number;
  unlogged: number;
}

export const TOP_PLAY_COUNT = 5;

/** Who a question is addressed to, written out. */
export function whoText(who: StakeholderId[]): string {
  const label = new Map(resolveDeepDiveStakeholders().map((p) => [p.id, p.label]));
  return who.map((id) => label.get(id) ?? id).join(' / ');
}

/**
 * The scenario this workshop runs.
 *
 * What the SE recorded on the proposal call wins. Failing that, Profiling has
 * already answered half of it: an account with no Cisco IQ entitlement can only
 * be the demo-led scenario, so there is nothing to choose and the deep-dive
 * loads it directly. With nothing recorded and no explicit decline, default to
 * the `not-enabled` scenario rather than leaving the deep-dive unselected — an
 * un-chosen workshop should present the safe baseline, not an empty screen.
 */
export function chosenScenario(state: WorkshopState): IqScenario | undefined {
  const id = state.discoveryAnswers['2-iq-scenario'];
  if (resolveIqScenarios().some((scenario) => scenario.id === id)) return id as IqScenario;
  if (state.connect.iq.declined) return 'not-enabled';
  return 'not-enabled';
}

function statusOf(response: GuideResponse | undefined): FindingStatus | null {
  if (!response) return null;
  if (response.noAnswer) return 'open';
  if (response.band === 'red') return 'gap';
  if (response.band === 'amber') return 'partial';
  if (response.band === 'green') return 'covered';
  return null;
}

/**
 * The phrases recorded against an answer, resolved back to their wording.
 *
 * Cues are stored as the phrase itself rather than an index, so re-importing
 * the guide cannot silently repoint a recorded phrase at a different one.
 * Numeric entries are read as indices for anything captured before that.
 */
function heardIn(question: DeepDiveQuestion, response: GuideResponse | undefined): string[] {
  const picked = response?.cues ?? [];
  if (!picked.length) return [];
  const band = response?.band;
  const known = band ? question.bands[BAND_OF[band]].cues : [];
  return picked.map((cue) => {
    const index = Number(cue);
    return cue !== '' && Number.isInteger(index) && known[index] ? known[index] : cue;
  });
}

export function deepDiveAnalysis(state: WorkshopState): DeepDiveAnalysis {
  const responses = state.guideResponses;
  const chosen = chosenScenario(state);
  const scenario = chosen ? resolveScenarioById().get(chosen) : undefined;

  const findings: DeepDiveFinding[] = [];
  const sections: SectionFinding[] = [];
  const groups: GroupFinding[] = [];

  for (const phase of scenario?.phases ?? []) {
    const group: GroupFinding = {
      id: phase.id,
      label: phase.label,
      gaps: 0,
      partials: 0,
      covered: 0,
      open: 0,
      asked: 0,
      total: 0,
    };

    for (const section of phase.sections) {
      const tally: SectionFinding = {
        id: section.id,
        code: section.code,
        short: section.short,
        title: section.title,
        phase: phase.id,
        phaseLabel: phase.label,
        gaps: 0,
        partials: 0,
        covered: 0,
        open: 0,
        unlogged: 0,
        asked: 0,
        total: section.questions.length,
        exposure: 0,
      };
      group.total += section.questions.length;

      for (const question of section.questions) {
        const response = responses[question.id];
        const status = statusOf(response);
        if (!status) {
          tally.unlogged += 1;
          continue;
        }

        findings.push({
          id: question.id,
          topic: section.short,
          sectionId: section.id,
          who: question.who,
          phase: phase.id,
          phaseLabel: phase.label,
          status,
          weight: status === 'gap' ? WEIGHT.red : status === 'partial' ? WEIGHT.amber : 0,
          heard: heardIn(question, response),
          note: response?.note?.trim() ?? '',
        });

        tally.asked += 1;
        group.asked += 1;
        if (status === 'gap') {
          tally.gaps += 1;
          group.gaps += 1;
        } else if (status === 'partial') {
          tally.partials += 1;
          group.partials += 1;
        } else if (status === 'covered') {
          tally.covered += 1;
          group.covered += 1;
        } else {
          tally.open += 1;
          group.open += 1;
        }
      }

      // A gap counts double a partial, measured against the questions that got
      // a band at all. Unanswered questions are left out so a lane that was
      // barely asked does not read as safe.
      const banded = tally.gaps + tally.partials + tally.covered;
      tally.exposure = banded
        ? Math.round(((tally.gaps * 2 + tally.partials) / (banded * 2)) * 100)
        : 0;
      sections.push(tally);
    }

    groups.push(group);
  }

  const stakeholders: StakeholderFinding[] = resolveDeepDiveStakeholders().map((person) => {
    const scoped = (scenario?.phases ?? []).flatMap((phase) =>
      phase.sections.flatMap((section) =>
        section.questions.filter((question) => question.who.includes(person.id)),
      ),
    );
    const mine = findings.filter((finding) => finding.who.includes(person.id));
    return {
      who: person.id,
      label: person.label,
      tier: person.tier,
      gaps: mine.filter((f) => f.status === 'gap').length,
      partials: mine.filter((f) => f.status === 'partial').length,
      covered: mine.filter((f) => f.status === 'covered').length,
      open: mine.filter((f) => f.status === 'open').length,
      asked: mine.length,
      total: scoped.length,
    };
  });

  const plays = rankPlays(scenario, responses);
  const count = (status: FindingStatus) => findings.filter((f) => f.status === status).length;
  const total = (scenario?.phases ?? []).reduce(
    (n, phase) => n + phase.sections.reduce((m, section) => m + section.questions.length, 0),
    0,
  );

  return {
    scenario,
    scenarioLabel: resolveIqScenarios().find((s) => s.id === scenario?.id)?.label ?? 'Not recorded',
    scenarioSetup: scenario?.setup ?? '',
    sections,
    groups,
    stakeholders,
    findings,
    plays,
    topPlays: plays.slice(0, TOP_PLAY_COUNT),
    restPlays: plays.slice(TOP_PLAY_COUNT),
    followUps: findings.filter((finding) => finding.status === 'open'),
    asked: findings.length,
    total,
    gaps: count('gap'),
    partials: count('partial'),
    covered: count('covered'),
    open: count('open'),
    unlogged: total - findings.length,
  };
}

/**
 * Every play the recorded answers point at, heaviest first.
 *
 * A play is not a judgement the tool makes. Each band of each question already
 * names what that answer points at, so the ranking is arithmetic over the
 * guide's own mapping.
 */
function rankPlays(
  scenario: DeepDiveScenario | undefined,
  responses: Record<string, GuideResponse>,
): RankedPlay[] {
  const acc = new Map<string, RankedPlay>();
  const playById = resolvePlayById();

  for (const phase of scenario?.phases ?? []) {
    for (const section of phase.sections) {
      for (const question of section.questions) {
        const response = responses[question.id];
        if (!response || response.noAnswer) continue;
        const band = response.band;
        if (band !== 'red' && band !== 'amber') continue;

        const status = band === 'red' ? 'gap' : 'partial';
        for (const id of question.bands[BAND_OF[band]].plays) {
          const play = playById.get(id);
          if (!play) continue;
          const entry = acc.get(id) ?? { play, score: 0, from: [] };
          entry.score += WEIGHT[band];
          if (!entry.from.some((f) => f.id === question.id)) {
            entry.from.push({ id: question.id, status, topic: section.short });
          }
          acc.set(id, entry);
        }
      }
    }
  }

  return [...acc.values()].sort((a, b) => b.score - a.score || b.from.length - a.from.length);
}

/** How many findings/plays to name in the AI context before it stops being compact. */
const CONTEXT_FINDING_CAP = 12;
const CONTEXT_FOLLOWUP_CAP = 6;
const CONTEXT_SECTION_CAP = 5;

/**
 * A compact, plain-text digest of the deep-dive, for grounding the AI report prose.
 *
 * The deep-dive is the only part of the workshop that actually measured this customer, but its
 * answers (`discoveryAnswers` `4-dd*` → `guideResponses`) were never sent to the report AI, so the
 * generated sections read generically. This folds the same material the deterministic drafters emit
 * (scenario, coverage tally, most-exposed lanes, the gaps/partials found, open follow-ups, and the
 * ranked plays) into a short steering block that is threaded as `additionalContext` on first
 * generation — so the AI writes about what was found in the room, not a template account.
 *
 * Bounded on purpose (caps above): this is grounding, not a data dump. Returns '' when no scenario
 * ran or nothing was logged, so an un-run deep-dive adds no context and the report is unchanged.
 */
export function deepDiveReportContext(state: WorkshopState): string {
  const a = deepDiveAnalysis(state);
  if (!a.scenario || a.asked === 0) return '';

  const parts: string[] = [];
  parts.push(
    'Deep-dive assessment findings for this account. Use them to ground the report; do not restate this list verbatim.',
  );
  parts.push(
    `Scenario: ${a.scenarioLabel}${a.scenarioSetup ? ` — ${a.scenarioSetup}` : ''}`,
  );
  parts.push(
    `Coverage: ${a.asked} of ${a.total} questions logged — ${a.gaps} gaps, ${a.partials} partial, ${a.covered} already covered, ${a.open} open follow-ups.`,
  );

  const exposed = a.sections
    .filter((s) => s.asked > 0)
    .sort((x, y) => y.exposure - x.exposure)
    .slice(0, CONTEXT_SECTION_CAP);
  if (exposed.length > 0) {
    parts.push(
      ['Most-exposed areas:']
        .concat(
          exposed.map(
            (s) =>
              `- ${s.short} (${s.phaseLabel}): exposure ${s.exposure}%, ${s.gaps} gap / ${s.partials} partial`,
          ),
        )
        .join('\n'),
    );
  }

  const issues = a.findings.filter((f) => f.status === 'gap' || f.status === 'partial');
  if (issues.length > 0) {
    parts.push(
      ['Gaps and partials found:']
        .concat(
          issues.slice(0, CONTEXT_FINDING_CAP).map((f) => {
            const detail = f.note || f.heard.join('; ');
            return `- ${f.topic} [${FINDING_STATUS_TEXT[f.status]}]${detail ? `: ${detail}` : ''}`;
          }),
        )
        .join('\n'),
    );
  }

  if (a.followUps.length > 0) {
    parts.push(
      ['Open follow-ups (asked, not yet answered):']
        .concat(
          a.followUps.slice(0, CONTEXT_FOLLOWUP_CAP).map((f) => {
            const detail = f.note || f.heard.join('; ');
            return `- ${f.topic}${detail ? `: ${detail}` : ''}`;
          }),
        )
        .join('\n'),
    );
  }

  if (a.topPlays.length > 0) {
    parts.push(
      ['Recommendations the answers point at hardest (heaviest first):']
        .concat(a.topPlays.map((p, i) => `${i + 1}. ${p.play.name} — ${p.play.detail}`))
        .join('\n'),
    );
  }

  return parts.join('\n\n');
}

/** Every section in the guide, deduplicated. The shared ones appear in all three. */
export function allSections(): DeepDiveSection[] {
  const seen = new Map<string, DeepDiveSection>();
  for (const scenario of resolveDeepDiveScenarios()) {
    for (const phase of scenario.phases) {
      for (const section of phase.sections) seen.set(section.id, section);
    }
  }
  return [...seen.values()];
}

/**
 * How to order what the answers pointed at.
 *
 * Ranking says what to talk about; this says what to talk about first, and it
 * is the part an SE gets wrong on their own. Selling the assessment before the
 * support tier that gates it, or the agent roadmap as though it shipped, are
 * both mistakes the guide anticipates.
 *
 * A rule fires when any of its plays ranked. A phase with rules of its own
 * replaces the shared list rather than adding to it, which is how the console
 * behaves: inside Act, the Act guidance is the guidance.
 */
export function sequencingNote(plays: RankedPlay[], phase: DeepDivePhase): string[] {
  const ranked = new Set(plays.map((entry) => entry.play.id));
  const sequencing = resolveSequencing();
  const fires = (rule: (typeof sequencing)[number]) =>
    rule.plays.some((id) => ranked.has(id));

  const scoped = sequencing.filter((rule) => rule.phase === phase && fires(rule));
  if (scoped.length > 0) return scoped.map((rule) => rule.text);

  const shared = sequencing.filter((rule) => rule.phase === 'any' && fires(rule));
  return shared.length > 0 ? shared.map((rule) => rule.text) : [resolveSequencingFallback()];
}

/** Where to take the conversation once a phase is done. */
export function closingCards(phase: DeepDivePhase) {
  return resolveClosing()[phase] ?? [];
}
